'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import gsap from 'gsap';
import { CarouselButton } from '../shared';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import {
  isBackdropFrozen,
  subscribeBackdropFreeze,
} from 'utils/freezeBackdropForMenu';
import dynamic from 'next/dynamic';
import { PlayWhite, CrossDark } from 'assets/icons';
import styles from './HeroCarousel.module.css';

type CarouselSlideFC = React.ComponentType<any>;

type VideoConfig = {
  desktop: string;
  mobile: string;
  thumbnail: string;
};

type CarouselItemConfig = {
  component: CarouselSlideFC;
  handleTheme?: 'light' | 'dark';
  handleRounded?: boolean | 'sm' | 'lg' | 'full';
  handleFill?: boolean;
  video?: VideoConfig;
  activeCountries?: string[];
};

type Props = {
  items: Array<React.ReactNode | CarouselSlideFC | CarouselItemConfig>;
  autoplayDelay?: number; // in ms
  loop?: boolean;
  className?: string;
  stopOnInteraction?: boolean;
  autoplayStopAtEnd?: boolean; // when true and loop is false, stop autoplay on last slide
  ariaLabel?: string;
};

const isItemConfig = (item: any): item is CarouselItemConfig => {
  return Boolean(item) && typeof item === 'object' && 'component' in item;
};

type HeroCarouselItem = Props['items'][number];

const renderHeroCarouselItem = (
  node: HeroCarouselItem,
  options?: { openModal?: () => void },
): React.ReactNode => {
  const openModal = options?.openModal;
  if (typeof node === 'function') {
    return React.createElement(node as CarouselSlideFC, { openModal });
  }
  if (isItemConfig(node)) {
    return React.createElement(node.component, { openModal });
  }
  if (React.isValidElement(node)) {
    return React.cloneElement(node as React.ReactElement<{ openModal?: () => void }>, {
      openModal,
    });
  }
  return node;
};

/** Single slide — skip Embla/autoplay when carousel controls are not needed. */
const HeroSingleSlide = ({
  className,
  items,
  ariaLabel = 'Featured content',
}: Pick<Props, 'className' | 'items' | 'ariaLabel'>) => {
  const item = items[0];
  if (!item) return null;

  return (
    <div className={className} aria-label={ariaLabel}>
      {renderHeroCarouselItem(item)}
    </div>
  );
};

// Constants
const DESKTOP_BREAKPOINT = 768;

// Defer heavy video player from initial bundle
const DynamicVideoJS = dynamic(() => import('components/heroes/VideoJS'), {
  ssr: false,
});

const HeroCarouselMulti = ({
  className,
  items,
  autoplayDelay = 6000,
  loop = true,
  stopOnInteraction = false,
  autoplayStopAtEnd = false,
  ariaLabel = 'Featured content carousel',
}: Props) => {
  const t = useTranslations('homepage');
  const { width, height } = useWindowSize();
  const isMobile = useMemo(() => width < DESKTOP_BREAKPOINT, [width]);
  // Same strip offset the slides use, so the arrows can centre on the card.
  const bannerPx = useMarketingBannerOffset();

  // Filter items by country to get visible items count
  const visibleItems = items;

  const autoplay = useMemo(
    () =>
      Autoplay({
        delay: autoplayDelay,
        stopOnInteraction: stopOnInteraction,
        playOnInit: true,
        stopOnLastSnap: autoplayStopAtEnd,
      }),
    [autoplayDelay, stopOnInteraction, autoplayStopAtEnd],
  );
  const autoplayRef = useRef<any>(null);
  const shouldShowControls = visibleItems.length > 1;
  const plugins = useMemo(
    () => (shouldShowControls ? [autoplay] : []),
    [autoplay, shouldShowControls],
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      align: 'center',
      slidesToScroll: 1,
    },
    plugins,
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const progressTween = useRef<gsap.core.Tween | null>(null);
  const progressRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Centralized video state
  const [expanded, setExpanded] = useState(false);
  const [mobileVideo, setMobileVideo] = useState(false);
  // no portal: no need for mounted guard

  const selectedItem = useMemo(
    () => items[selectedIndex] as any,
    [items, selectedIndex],
  );
  const selectedVideo: VideoConfig | null = useMemo(() => {
    if (!selectedItem) return null;
    if (typeof selectedItem === 'function') return null;
    if (isItemConfig(selectedItem)) return selectedItem.video ?? null;
    return null;
  }, [selectedItem]);

  // Mini player visibility control
  const hasVideo = Boolean(selectedVideo);
  const [showMiniContainer, setShowMiniContainer] = useState(hasVideo);
  const [containerFadingOut, setContainerFadingOut] = useState(false);

  const sources = useMemo(() => {
    if (!selectedVideo) return null;
    return {
      desktop: getCompressedAssetUrl(selectedVideo.desktop),
      mobile: getCompressedAssetUrl(selectedVideo.mobile),
      thumbnail: getCompressedAssetUrl(selectedVideo.thumbnail),
    };
  }, [selectedVideo]);

  const activeSrc = useMemo(() => {
    if (!sources) return '';
    if (!expanded) return sources.thumbnail;
    return isMobile ? sources.mobile : sources.desktop;
  }, [expanded, isMobile, sources]);

  // When the selected slide doesn't have a video, fade out the mini player; otherwise keep it visible
  useEffect(() => {
    if (hasVideo) {
      setShowMiniContainer(true);
      setContainerFadingOut(false);
      return;
    }
    // Fade out then unmount
    if (showMiniContainer) {
      setContainerFadingOut(true);
      const t = setTimeout(() => {
        setShowMiniContainer(false);
        setContainerFadingOut(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [hasVideo, showMiniContainer]);

  // preload sources for active slide
  useEffect(() => {
    if (!sources) return;
    const vids: HTMLVideoElement[] = [];
    const preload = (src: string) => {
      const v = document.createElement('video');
      v.src = src;
      v.preload = 'auto';
      v.muted = true;
      v.playsInline = true;
      vids.push(v);
    };
    preload(isMobile ? sources.mobile : sources.desktop);
    preload(sources.thumbnail);
    return () => {
      vids.forEach((v) => {
        v.pause();
        v.src = '';
        v.load();
      });
    };
  }, [sources, isMobile]);

  const openVideo = useCallback(() => {
    setExpanded(true);
    if (isMobile) setMobileVideo(true);
    // Stop autoplay while video is open
    if (autoplayRef.current && typeof autoplayRef.current.stop === 'function') {
      try {
        autoplayRef.current.stop();
      } catch (err) {
        console.warn('Failed to stop autoplay:', err);
      }
    }
  }, [isMobile]);

  const closeVideo = useCallback(() => {
    setExpanded(false);
    setMobileVideo(false);
    // Resume autoplay after closing video if controls are enabled
    if (
      shouldShowControls &&
      autoplayRef.current &&
      typeof autoplayRef.current.play === 'function'
    ) {
      try {
        autoplayRef.current.play();
      } catch (err) {
        console.warn('Failed to resume autoplay:', err);
      }
    }
  }, [shouldShowControls]);

  // no-op: kept placeholder in case of future SSR guards

  const videoOptions = useMemo(() => {
    if (!sources) return undefined as any;
    return {
      autoplay: isMobile ? expanded : true,
      controls: expanded,
      responsive: true,
      fluid: true,
      sources: activeSrc ? [{ src: activeSrc, type: 'video/mp4' }] : [],
      playsinline: true,
      preload: 'auto',
      muted: !expanded,
      loop: !expanded && !isMobile,
      html5: {
        vhs: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    } as const;
  }, [activeSrc, expanded, isMobile, sources]);

  const activeButtonProps = useMemo(() => {
    const fallback = {
      variant: 'light' as const,
      rounded: 'full' as const,
      fill: true,
    };
    if (!items || items.length === 0) return fallback;
    const item = items[selectedIndex] as any;
    if (isItemConfig(item)) {
      const resolveRounded = () => {
        const r = item.handleRounded;
        if (typeof r === 'boolean') return r ? 'full' : 'sm';
        return (r ?? 'full') as 'sm' | 'lg' | 'full';
      };
      return {
        variant: (item.handleTheme ?? 'light') as 'light' | 'dark',
        rounded: resolveRounded(),
        fill: item.handleFill ?? true,
      };
    }
    return fallback;
  }, [items, selectedIndex]);

  const killProgressTween = useCallback(() => {
    if (progressTween.current) {
      progressTween.current.kill();
      progressTween.current = null;
    }
  }, []);

  const resetAllBars = useCallback(() => {
    progressRefs.current.forEach((el) => {
      if (el) {
        gsap.set(el, { clearProps: 'all', width: '0%' });
      }
    });
  }, []);

  const startProgressForActive = useCallback(
    (durationMs: number) => {
      killProgressTween();
      resetAllBars();
      const activeEl = progressRefs.current[selectedIndex];
      if (!activeEl) return;
      const startDelaySeconds = 0.24; // wait for dot width transition
      gsap.set(activeEl, { width: '0%' });
      progressTween.current = gsap.to(activeEl, {
        width: '100%',
        duration: Math.max(0, durationMs) / 1000,
        ease: 'none',
        delay: startDelaySeconds,
      });
    },
    [killProgressTween, resetAllBars, selectedIndex],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index);
    // Reset any mobile-only mini video when slide changes
    setMobileVideo(false);
    setExpanded(false);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    killProgressTween();
    emblaApi.scrollPrev();
    // Keep autoplay timing consistent after programmatic navigation
    if (
      shouldShowControls &&
      autoplayRef.current &&
      typeof autoplayRef.current.reset === 'function'
    ) {
      try {
        autoplayRef.current.reset();
      } catch (err) {
        console.warn('Failed to reset autoplay:', err);
      }
    }
  }, [emblaApi, killProgressTween, shouldShowControls]);
  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    killProgressTween();
    emblaApi.scrollNext();
    if (
      shouldShowControls &&
      autoplayRef.current &&
      typeof autoplayRef.current.reset === 'function'
    ) {
      try {
        autoplayRef.current.reset();
      } catch (err) {
        console.warn('Failed to reset autoplay:', err);
      }
    }
  }, [emblaApi, killProgressTween, shouldShowControls]);

  // Open video for a specific slide index after ensuring the carousel has settled
  const openAfterScrollTo = useCallback(
    (targetIndex: number) => {
      if (!emblaApi) {
        openVideo();
        return;
      }
      const current = emblaApi.selectedScrollSnap();
      if (current === targetIndex) {
        openVideo();
        return;
      }
      const handleSettle = () => {
        openVideo();
        emblaApi.off('settle', handleSettle);
      };
      emblaApi.on('settle', handleSettle);
      emblaApi.scrollTo(targetIndex);
    },
    [emblaApi, openVideo],
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    // Safely retrieve autoplay plugin
    const pluginsObj = emblaApi.plugins();
    autoplayRef.current = pluginsObj?.autoplay || null;

    // Use Autoplay plugin events to synchronize the progress
    const handleTimerSet = () => {
      if (
        !autoplayRef.current ||
        typeof autoplayRef.current.timeUntilNext !== 'function'
      )
        return;
      const remaining = autoplayRef.current.timeUntilNext();
      startProgressForActive(remaining ?? autoplayDelay);
    };
    const handleTimerStopped = () => {
      killProgressTween();
    };

    emblaApi.on('autoplay:timerset', handleTimerSet);
    emblaApi.on('autoplay:timerstopped', handleTimerStopped);
    emblaApi.on('autoplay:stop', handleTimerStopped);

    // Start the autoplay once Embla is ready
    if (
      shouldShowControls &&
      autoplayRef.current &&
      typeof autoplayRef.current.play === 'function'
    ) {
      try {
        // Delay autoplay start to ensure plugin is fully initialized
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(() => {
            try {
              if (
                autoplayRef.current &&
                typeof autoplayRef.current.play === 'function'
              ) {
                autoplayRef.current.play();
              }
              // Safari quirk: ensure the internal timer is armed post-layout
              if (
                autoplayRef.current &&
                typeof autoplayRef.current.reset === 'function'
              ) {
                autoplayRef.current.reset();
              }
            } catch (err) {
              console.warn('Failed to start autoplay:', err);
            }
          });
        }
      } catch (err) {
        console.warn('Failed to initialize autoplay:', err);
      }
    }

    // Resume/arm autoplay when tab becomes visible (helps Safari)
    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        shouldShowControls &&
        autoplayRef.current &&
        typeof autoplayRef.current.reset === 'function'
      ) {
        try {
          autoplayRef.current.reset();
        } catch (err) {
          console.warn('Failed to reset autoplay on visibility change:', err);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('autoplay:timerset', handleTimerSet);
      emblaApi.off('autoplay:timerstopped', handleTimerStopped);
      emblaApi.off('autoplay:stop', handleTimerStopped);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [
    emblaApi,
    onSelect,
    startProgressForActive,
    killProgressTween,
    shouldShowControls,
    autoplayDelay,
  ]);

  // Pause slide advances while the header menu frost is up so the scrim
  // isn't resampling a changing hero every few seconds.
  useEffect(() => {
    if (!shouldShowControls) return;
    const sync = (frozen: boolean) => {
      if (!autoplayRef.current) return;
      try {
        if (frozen) {
          if (typeof autoplayRef.current.stop === 'function') {
            autoplayRef.current.stop();
          }
          return;
        }
        if (
          !expanded &&
          document.visibilityState === 'visible' &&
          typeof autoplayRef.current.play === 'function'
        ) {
          autoplayRef.current.play();
        }
      } catch (err) {
        console.warn('Failed to sync autoplay with menu freeze:', err);
      }
    };
    if (isBackdropFrozen()) sync(true);
    return subscribeBackdropFreeze(sync);
  }, [shouldShowControls, expanded]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!expanded) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeVideo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded, closeVideo]);

  return (
    <div
      className={`${className ?? ''} ${expanded ? 'is-video-expanded' : ''}`}
      style={{ '--campaign-banner-px': `${bannerPx}px` } as React.CSSProperties}
      role="group"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      aria-live="off"
    >
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {items.map((node, idx) => {
            const provideOpen = () => {
              // Ensure carousel settles on the target slide before opening
              openAfterScrollTo(idx);
            };
            const eager = idx <= 1;
            const rendered =
              typeof node === 'function'
                ? React.createElement(node as CarouselSlideFC, {
                    openModal: provideOpen,
                    eager,
                  })
                : isItemConfig(node)
                ? React.createElement((node as CarouselItemConfig).component, {
                    openModal: provideOpen,
                    eager,
                  })
                : React.isValidElement(node)
                ? React.cloneElement(node as any, {
                    openModal: provideOpen,
                    eager,
                  })
                : (node as any);
            return (
              <div
                className="embla__slide"
                key={idx}
                role="group"
                aria-roledescription="slide"
                aria-label={t('homepage.heroCarousel.aria.slide', {
                  current: idx + 1,
                  total: items.length,
                })}
              >
                {rendered}
              </div>
            );
          })}
        </div>
      </div>

      {/* Single floating video container outside of slides; fades between thumbnails on slide change */}
      {showMiniContainer && (isMobile ? mobileVideo || expanded : true) && (
        <div
          className={`${styles.videoFloatingContainer} ${
            expanded ? 'expanded' : ''
          } ${containerFadingOut ? 'is-fading-out' : ''}`}
          style={{
            position: expanded ? 'fixed' : 'absolute',
            zIndex: expanded ? 9999 : 100,
            bottom: expanded ? '50%' : '3%',
            right: expanded ? '50%' : '3%',
            transform: expanded ? 'translate(50%, 50%)' : 'none',
            width: expanded ? width * 0.9 + 'px' : '280px',
            height: expanded
              ? Math.min(height * 0.9, height * 0.75) + 'px'
              : '157px',
          }}
        >
          {videoOptions && (
            <DynamicVideoJS key={activeSrc} options={videoOptions} />
          )}
          {!isMobile && !expanded && (
            <button
              className={styles.playOverlay}
              onClick={openVideo}
              aria-label={t('homepage.heroCarousel.aria.playVideo')}
              data-buttontype="animation replay"
            >
              <PlayWhite />
            </button>
          )}
          {expanded && (
            <button
              className={styles.closeButton}
              onClick={closeVideo}
              aria-label={t('homepage.heroCarousel.aria.closeVideo')}
            >
              <CrossDark
                style={{ width: '1rem', height: '1rem' }}
                fill="white"
              />
            </button>
          )}
        </div>
      )}

      {shouldShowControls && (
        <>
          <div className="nav-btn prev">
            <CarouselButton
              onClick={scrollPrev}
              aria-label={t('homepage.heroCarousel.aria.previous')}
              direction="prev"
              variant={activeButtonProps.variant}
              rounded={activeButtonProps.rounded}
              fill={activeButtonProps.fill}
            />
          </div>
          <div className="nav-btn next">
            <CarouselButton
              onClick={scrollNext}
              aria-label={t('homepage.heroCarousel.aria.next')}
              direction="next"
              variant={activeButtonProps.variant}
              rounded={activeButtonProps.rounded}
              fill={activeButtonProps.fill}
            />
          </div>

          <div className="indicator-wrapper" aria-hidden="true">
            {visibleItems.map((_, i) => (
              <div
                className={`dot ${i === selectedIndex ? 'is-active' : ''}`}
                key={i}
              >
                <div
                  className="progress"
                  ref={(el) => {
                    progressRefs.current[i] = el;
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {expanded && (
        <div
          className={styles.videoOverlay}
          data-visible="true"
          onClick={closeVideo}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

const HeroCarousel = (props: Props) => {
  const className = [styles.heroCarousel, props.className]
    .filter(Boolean)
    .join(' ');
  const mergedProps = { ...props, className };
  if (props.items.length === 0) return null;
  if (props.items.length === 1) {
    return <HeroSingleSlide {...mergedProps} />;
  }
  return <HeroCarouselMulti {...mergedProps} />;
};

export default HeroCarousel;
